/**
 * @file   src\Pages\CWOMyProfile.tsx
 * @brief  MyProfile page.
 * @date   JUL, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */

import { React, useState, useEffect, useOutletContext, Container, Button, Row, Col, useNavigate, Tab, Tabs, useLocation, useRef, Modal } from '../utils/thirdpartyComponents';
import "../styles/subscriptions.scss";
import CWSelect from '../components/CWSelect';
import CWInput from '../components/CWInput';
import CWPasswordInput from '../components/CWPasswordInput';
import { pageURLs } from '../utils/pageUrls';
import { AppContextType , CompanyDetailsItem } from '../interfaces/generalInterface';
import * as Yup from 'yup'
import { MYPROFILE_SCHEMA, CHANGE_PASSWORD_SCHEMA, LOCATIONMANAGERMYPROFILE_SCHEMA } from '../validations/cwoprofileSchema';
import { EMAIL_MAX_LENGTH, NAME_MAX_LENGTH, PASSWORD_MAX_LENGTH, CITY_MAX_LENGTH, WASH_ADDRESS_MAX_LENGTH, WASH_NAME_MAX_LENGTH, PHONE_MAX_LENGTH,  ACCOUNT_NUMBER_MAX_LENGTH, ROUTING_NUMBER_MAX_LENGTH} from '../utils/constants';
import { CountryItem, Option } from '../interfaces/CarWashInterface';
import { formatPhoneNumber, formatPhoneNumberforApi} from '../utils/helper';
import axios from '../config/axiosConfig';
import { UsersApi, CompanyApi, LocationManagerApi } from '../utils/apiUrls';
import { fetchStatesByCountry, fetchSupportedCountries, getCountryCodeFromRecord, getCountryFieldLabels, getPostalCodeMaxLength, getSelectedOptions, isAllowedPostalCodeInput, mapCountryOptions, normalizePostalCodeInput } from '../utils/countryState';
import { MessageToaster } from '../utils/toastUtil';
import SmallLoader from '../components/SmallLoader';
import { AccountHolderType } from '../utils/enums';
import { useStripe } from '@stripe/react-stripe-js';
import { cwoaddACHBankAccountSchema } from '../validations/cwoaddCarwashSchema';
import moment from "moment-timezone";
import { getFromLocalStorage } from '../utils/helper';


const CWOMyProfile = ()  => {

    const stripe = useStripe();
    const [loading, setLoading] = useState(false);
    const location = useLocation();

    const storageData = getFromLocalStorage('CARWASH_OWNER_DATA');
    const userInfo = storageData && storageData !== null ? storageData : null;
    const locationId = Number(userInfo?.locationId || userInfo?.locationID || userInfo?.LocationId || userInfo?.LocationID || 0);

    // Use a ref to store input elements
    const formRef = useRef<HTMLFormElement>(null);
    const form2Ref = useRef<HTMLFormElement>(null);
    const form3Ref = useRef<HTMLFormElement>(null);
    
    const query = new URLSearchParams(location.search);
    const tab = query.get('tab') || 'profile';
    const defaulttab = (tab && ['profile','changepassword'].includes(tab)) ? tab : 'profile'
    // Instantiate MessageToaster
    const toaster = new MessageToaster();

    // State to handle options for country and state
    const [supportedCountries, setSupportedCountries] = useState<CountryItem[]>([]);
    const [options, setOptions] = useState<Option[]>([]);
    const [bankInformationVerificationStatus, setBankInformationVerificationStatus] = useState(false);
    const [bankInformationVerificationLink, setBankInformationVerificationLink] = useState(null);
    const [bankAccountLast4Digit, setBankAccountLast4Digit] = useState<any>(null);
    const [selectedTab, setSelectedTab] = useState(defaulttab);

    // Access the context
    const { updateData } = useOutletContext<AppContextType>();
    const [companyDetails, setCompanyDetails] = useState<CompanyDetailsItem>({
        SubscriptionPaymentDate: null,
        SubscriptionStartDate: null,
        carWashGUID: '',
        carWashURL: '',
        hasUrlPermission: 0,
        subscriptionActive: 0
    })

    const navigate = useNavigate();

    // Convert AccountHolderType enum to an array of options
    const accountholderOptions = [
        { label: 'Select', value: 0 }, // First option with value 0
        ...Object.keys(AccountHolderType).filter(key => !isNaN(Number(key))).map(key => ({
            label: AccountHolderType[key as keyof typeof AccountHolderType],
            value: Number(key)
        }))
    ];

    // Initialize states for profile formdata
    const [profileformData, setProfileFormData] = useState({
        washname: '',
        address1: '',
        address2: '',
        city: '',
        country: '',
        state: '',
        zipcode: '',
        name: '',
        email: '',
        phonenumber: '',
    })
    const countryOptions = mapCountryOptions(supportedCountries);
    const { stateLabel, postalLabel } = getCountryFieldLabels(supportedCountries, profileformData.country);
    const postalMaxLength = getPostalCodeMaxLength(profileformData.country);

    // Handle profile errors
    const [profileerrors, setProfileErrors] = useState({
        washname: '',
        address1: '',
        address2: '',
        city: '',
        country: '',
        state: '',
        zipcode: '',
        name: '',
        email: '',
        phonenumber: '',
    });

    // Initialize states for payment info formdata
    const [paymentinfoformData, setPaymentinfoFormData] = useState({
        accountholdername: '',
        accountnumber: '',
        retypeaccountnumber:'',
        routingnumber: '',
        accountholdertype : 1

    })

    // Handle payment info errors
    const [paymentinfoerrors, setPaymentinfoErrors] = useState({
        accountholdername: '',
        accountnumber: '',
        retypeaccountnumber: '',
        routingnumber: '',
        accountholdertype:'',
    });

    // Initialize states for password formdata
    const [passwordformData, setPasswordFormData] = useState({
        oldpassword: '',
        newpassword: '',
        retypepassword: '',

    })

    // Handle password errors
    const [passworderrors, setPasswordErrors] = useState({
        oldpassword: '',
        newpassword: '',
        retypepassword: '',
    });


    const getCountries = async () => {
        try {
            const countries = await fetchSupportedCountries();
            setSupportedCountries(countries)
        } catch (error: any) {
            toaster.toastError(error.message);
        }
    }

    const getStates = async (countryCode: string) => {
        try {
            const stateOptions = await fetchStatesByCountry(countryCode);
            setOptions(stateOptions)
        } catch (error: any) {
            setOptions([])
            toaster.toastError(error.message);
        }
    }

    // To get company details
    const getCompanyAddress = async () => {
        try {
            const { data } = (userInfo?.userTypeId === 4 && userInfo?.permissionType === 2) ? await axios.get(LocationManagerApi.CARWASH_OWNER_GET_LOCATIONMANAGER_PROFILE) : await axios.get(CompanyApi.CARWASH_OWNER_COMPANY_ADDRESS);
            if (data?.error === 0) {
                const owner_profile = data.data;
                if(userInfo?.userTypeId === 4 && userInfo?.permissionType === 2)
                    {
                        setProfileFormData({
                            washname: "",
                            address1: "",
                            address2: "",
                            city: "",
                            country: "",
                            state: "",
                            zipcode: "",
                            name: owner_profile.FullName,
                            email: owner_profile.Email,
                            phonenumber: "",
                        });
                    }
                    else
                    {
                        const countryCode = getCountryCodeFromRecord(owner_profile);
                        const stateOptions = await fetchStatesByCountry(countryCode);
                        setOptions(stateOptions);
                        setProfileFormData({
                            washname: owner_profile.companyName,
                            address1: owner_profile.address1,
                            address2: owner_profile.address2,
                            city: owner_profile.city,
                            country: countryCode,
                            state: owner_profile.state,
                            zipcode: owner_profile.zipCode,
                            name: owner_profile.ContactName,
                            email: owner_profile.Email,
                            phonenumber: formatPhoneNumber(owner_profile.PhoneNumber),
                        });

                    }
            }

        } catch (error: any) {
            throw new Error(error.response?.data?.message || error.message);
        }
    }

     // To get company details
     const getCompanyBank = async () => {
        try {
            const { data } = await axios.get(`${UsersApi.CARWASH_OWNER_GET_BANK}/${locationId}`);
            if (data?.error === 0) {
               
                const owner_bank = data.data.paymentDetails;
                let accountHolderType = 1;
                if(owner_bank.accountHolder === 'company' ) {
                    accountHolderType = 2;
                }
                setPaymentinfoFormData({
                        accountholdername: owner_bank.billingName,
                        accountnumber: (owner_bank.last4) ? `**** **** **** ${owner_bank.last4}` : '',
                        retypeaccountnumber:(owner_bank.last4) ? `**** **** **** ${owner_bank.last4}` : '',
                        routingnumber: owner_bank.routingNumber,
                        accountholdertype: accountHolderType,
                });
          
            }

        } catch (error: any) {
            throw new Error(error.response?.data?.message || error.message);
        }
    }
    
    // To display heading
    useEffect(() => {
        handleUpdate();
        getCountries();
        getCompanyAddress();
        getCompany()
    }, []);

     // To display heading
     useEffect(() => {
        if(selectedTab === 'paymentinfo') {
            checkBankInfoVerificationStatus()
        }
    }, [selectedTab]);



    // Update the data
    const handleUpdate = () => {
        updateData({ heading: "My Profile", url: "", showBackButton: false, showMainMenu: true, activeLink: "manage-car-wash" });
    };

    // Handle the paste event to allow only 10 digits in phone number
    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('Text');
        // Extract only numbers and limit to 10 characters
        const formattedData = pastedData.replace(/\D/g, '').slice(0, 10);
        const formattedValue = formatPhoneNumber(formattedData);
        setProfileFormData({
            ...profileformData,
            phonenumber: formattedValue,
        });
    };

    // Triggers whenever options is selected
    const handleSelectChange = (selectedOption: any, name: string) => {
        const value = selectedOption ? selectedOption.value : '';

        // To remove validation when user enters input
        const keys = Object.keys(profileerrors);
        if (keys.includes(name)) {
            setProfileErrors({
                ...profileerrors,
                [name]: ""
            })
        }

        if (name === 'country') {
            setProfileErrors({
                ...profileerrors,
                country: '',
                state: '',
                zipcode: '',
            })
            setProfileFormData({
                ...profileformData,
                country: value,
                state: '',
                zipcode: '',
            });
            getStates(value);
            return;
        }

        setProfileFormData({
            ...profileformData,
            [name]: value,
        });

    };

    const onprofileInputChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target
        // To allow only digits in phone number field
        if ((event.target.name === "phonenumber")) {
            // Extract only digits from the input value
            const phone_value = event.target.value;
            if (!/^\d*$/.test(phone_value)) {
                event.target.value = phone_value.replace(/\D/g, '');
            }

            const { name, value } = event.target
            const formattedValue = formatPhoneNumber(value);

            setProfileFormData({
                ...profileformData,
                [name]: formattedValue,
            });
        } else if ((event.target.name === "zipcode")) {

            const new_value = normalizePostalCodeInput(event.target.value, profileformData.country);
            if (isAllowedPostalCodeInput(new_value, profileformData.country)) {
                setProfileFormData({
                    ...profileformData,
                    [name]: new_value,
                });
            }
        } else {
            setProfileFormData({
                ...profileformData,
                [name]: value,
            });

        }

        // To remove validation when user enters input
        const keys = Object.keys(profileerrors);
        if (keys.includes(name)) {
            setProfileErrors({
                ...profileerrors,
                [name]: ""
            })
        }
    };


    // Triggers whenever payment options is selected
    const handlepaymentSelectChange = (selectedOption: any, name: string) => {
        const value = selectedOption ? selectedOption.value : '';

        // To remove validation when user enters input
        const keys = Object.keys(paymentinfoerrors);
        if (keys.includes(name)) {
            setPaymentinfoErrors({
                ...paymentinfoerrors,
                [name]: ""
            })
        }

        setPaymentinfoFormData({
            ...paymentinfoformData,
            [name]: value,
        });

    };

    const onpaymentinfoInputChange = async (event: React.ChangeEvent<HTMLInputElement>) => {

        // To allow only digits in routingnumber field
        if (event.target.name === "routingnumber") {
            // Extract only digits from the input value
            const routingnumber_value = event.target.value;
            if (!/^\d*$/.test(routingnumber_value)) {
                event.target.value = routingnumber_value.replace(/\D/g, '');
            }
        }

        // To allow only digits in accountnumber field
        if (event.target.name === "accountnumber") {
            // Extract only digits from the input value
            const accountnumber_value = event.target.value;
            if (!/^\d*$/.test(accountnumber_value)) {
                event.target.value = accountnumber_value.replace(/\D/g, '');
            }
        }

        // To allow only digits in retypeaccountnumber field
        if (event.target.name === "retypeaccountnumber") {
            // Extract only digits from the input value
            const retypeaccountnumber_value = event.target.value;
            if (!/^\d*$/.test(retypeaccountnumber_value)) {
                event.target.value = retypeaccountnumber_value.replace(/\D/g, '');
            }
        }


        const { name, value } = event.target;

        setPaymentinfoFormData({
            ...paymentinfoformData,
            [name]: value,
        });


        // To remove validation when user enters input
        const keys = Object.keys(paymentinfoerrors);
        if (keys.includes(name)) {
            setPaymentinfoErrors({
                ...paymentinfoerrors,
                [name]: ""
            })
        }
    };

    const onpasswordInputChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;

        // To remove validation when user enters input
        const keys = Object.keys(passworderrors);
        if (keys.includes(name)) {
            setPasswordErrors({
                ...passworderrors,
                [name]: ""
            })
        }

        setPasswordFormData({
            ...passwordformData,
            [name]: value,
        });
    };

    const profilehandleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        try {
            setLoading(true);
            // Validate form data
            if(userInfo?.userTypeId === 4 && userInfo?.permissionType === 2)
            {
                await LOCATIONMANAGERMYPROFILE_SCHEMA.validate(profileformData, { abortEarly: false });
            }
            else
            {
                await MYPROFILE_SCHEMA.validate(profileformData, { abortEarly: false });
            }
            const newformData = {
                carWashName: profileformData.washname.trim(),
                address1: profileformData.address1.trim(),
                address2: profileformData.address2.trim(),
                contactName: profileformData.name.trim(),
                phoneNumber: profileformData.phonenumber.trim(),
                city: profileformData.city.trim(),
                countryCode: profileformData.country.trim(),
                stateCode: profileformData.state.trim(),
                zipCode: profileformData.zipcode.trim(),
            };

            // Modify formData to format phone number
            newformData.phoneNumber = formatPhoneNumberforApi(newformData.phoneNumber);
            // API call to sign up
            try {
                const { data } = (userInfo?.userTypeId === 4 && userInfo?.permissionType === 2) ? await axios.post(LocationManagerApi.CARWASH_OWNER_LOCATIONMANAGER_PROFILE_UPDATE, {"name": newformData.contactName}) : await axios.post(UsersApi.CARWASH_OWNER_MODIFY_PROFILE, newformData);

                if (data?.error === 0) {
                    setProfileFormData({
                        washname: '',
                        address1: '',
                        address2: '',
                        city: '',
                        country: '',
                        state: '',
                        zipcode: '',
                        name: '',
                        email: '',
                        phonenumber: '',
                    });

                    setProfileErrors({
                        washname: '',
                        address1: '',
                        address2: '',
                        city: '',
                        country: '',
                        state: '',
                        zipcode: '',
                        name: '',
                        email: '',
                        phonenumber: '',
                    });
                    getCompanyAddress();
                    toaster.toastSuccess(data?.message || "Your Profile Updated Successfully.");

                }
                else {
                    // show error message
                    toaster.toastError(data?.message || "An error occurred.");
                }
            } catch (error: any) {
                throw new Error(error?.response?.data?.message || error?.message);
            }
            finally {
                setLoading(false); // Hide loader
            }


        } catch (profileerrors) {
            const newErrors: any = {};

            // If validation fails on submit, then errors are shown
            if (profileerrors instanceof Yup.ValidationError) {
                profileerrors.inner.forEach((err) => {
                    newErrors[err.path as keyof typeof passwordformData] = err.message;
                });
                setProfileErrors(newErrors);
            }

            // Focus on the first invalid field
            if (formRef.current) {
                const firstInvalidField = Object.keys(newErrors)[0];
                const invalidElement = formRef.current.querySelector<HTMLInputElement>(`[name="${firstInvalidField}"]`);
                if (invalidElement) {
                    invalidElement.focus();
                }
            }
        }
        finally{
            setLoading(false);
        }
    }

    const paymentinfohandleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        try {
            setLoading(true);
            // Validate form data
            await cwoaddACHBankAccountSchema.validate(paymentinfoformData, { abortEarly: false });
            try {
                
                if (stripe) {
                    const responseclient_secret = await axios.get(`${UsersApi.CARWASH_OWNER_GET_CLIENT_SECRET}/${locationId}`);
                    if (responseclient_secret?.data?.error === 0) 
                    {
                        const clientSecret = responseclient_secret.data.data.clientSecret;
                        const setupIntentResult:any = await attachBankAccountToStripe(clientSecret)
                        console.log(setupIntentResult)
                        if(setupIntentResult?.setupIntent?.id) {
                            if (setupIntentResult?.setupIntent?.status === "requires_payment_method") {
                                toaster.toastError("Confirmation failed. Please try again with a different bank account.");
                            } else if(setupIntentResult?.setupIntent?.status === "succeeded") {
                                localStorage.setItem('CARWASH_OWNER_PAYMENTDATA_ADDED', 'true')
                                await updateCustomerPaymentMethod(setupIntentResult.setupIntent.payment_method);
                                setBankInformationVerificationStatus(false)
                            } else if(setupIntentResult?.setupIntent?.next_action?.type === "verify_with_microdeposits"){
                                setBankAccountLast4Digit(paymentinfoformData.accountnumber.slice(-4))
                                setBankInformationVerificationLink(setupIntentResult.setupIntent.next_action?.verify_with_microdeposits?.hosted_verification_url)
                                handleShowAddSuccessModal();
                                setBankInformationVerificationStatus(false)
                            } else {
                                const errorMessage:any = (setupIntentResult?.error) ? setupIntentResult?.error?.message : "Some error occured please try again !";
                                toaster.toastError(errorMessage);
                                setBankInformationVerificationStatus(false)
                            }
                        } else {
                            const errorMessage:any = (setupIntentResult?.error) ? setupIntentResult?.error.message : "Some error occured please try again !";
                            toaster.toastError(errorMessage);
                        }
                    }
                    

                } else {
                    console.error("Stripe is not initialized");
                    // Handle the error, such as showing an error message to the user
                }

                        
            } catch (paymentinfoerrors) {

               console.log(paymentinfoerrors)
            }
            finally{
                setLoading(false);
            }
        }  catch (paymentinfoerrors) {
            const newErrors: any = {};

            // If validation fails on submit, then errors are shown
            if (paymentinfoerrors instanceof Yup.ValidationError) {
                paymentinfoerrors.inner.forEach((err) => {
                    newErrors[err.path as keyof typeof paymentinfoformData] = err.message;
                });
                setPaymentinfoErrors(newErrors);
            }

            // Focus on the first invalid field
            if (form2Ref.current) {
                const firstInvalidField = Object.keys(newErrors)[0];
                const invalidElement = form2Ref.current.querySelector<HTMLInputElement>(`[name="${firstInvalidField}"]`);
                if (invalidElement) {
                    invalidElement.focus();
                }
            }
        } finally {
            setLoading(false)
        }
    }

    const passwordhandleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        try {
            // Validate form data
            await CHANGE_PASSWORD_SCHEMA.validate(passwordformData, { abortEarly: false });

            try {
                setLoading(true);
                const responseProfilepasswordUpdate = await axios.post(UsersApi.CARWASH_OWNER_MODIFY_PASSWORD, {
                    oldPassword: passwordformData.oldpassword,
                    newPassword: passwordformData.newpassword,
                    confirmPassword : passwordformData.retypepassword
                  });
                if (responseProfilepasswordUpdate?.data?.error === 0) {
                    
                    setPasswordErrors({
                        oldpassword: '',
                        newpassword: '',
                        retypepassword: '',
                    });
                    setPasswordFormData({
                        ...passwordformData,
                        oldpassword: '',
                        newpassword: '',
                        retypepassword: '',
                    });
                    toaster.toastSuccess(responseProfilepasswordUpdate?.data?.message);
                }
                else {
                    toaster.toastError(responseProfilepasswordUpdate?.data?.message);
                }
            } catch{
                toaster.toastError('Failed to update password.');
            }
            finally{
                setLoading(false);
            }


        } catch (passworderror) {
            const newErrors: any = {};

            // If validation fails on submit, then errors are shown
            if (passworderror instanceof Yup.ValidationError) {
                passworderror.inner.forEach((err) => {
                    newErrors[err.path as keyof typeof passwordformData] = err.message;
                });
                setPasswordErrors(newErrors);
            }

            // Focus on the first invalid field
            if (form3Ref.current) {
                const firstInvalidField = Object.keys(newErrors)[0];
                const invalidElement = form3Ref.current.querySelector<HTMLInputElement>(`[name="${firstInvalidField}"]`);
                if (invalidElement) {
                    invalidElement.focus();
                }
            }
        }
    }

    const getCompany = async () => {
        try {
            const { data } =  await axios.get(CompanyApi.CARWASH_OWNER_COMPANY_DETAILS);
            setCompanyDetails(data.data)
        } catch (error: any) {
            console.error('Error fetching company information:', error.message);
            throw new Error(error.response?.data?.message || error.message);
        }
    };

    const formatDate = (paymentDate: string) => {
        const date = moment(paymentDate);
        const formattedDate = date.format('MM/DD/YYYY');
        return formattedDate
    }


    //Update payment method to our DB
    const updateCustomerPaymentMethod = async (paymentMethodId : any) => {
        setLoading(true);
        const responseProfilepaymentUpdate = await axios.post(`${UsersApi.CARWASH_OWNER_MODIFY_BANK}/${locationId}`, {
            paymentMethod: paymentMethodId
        });
        if (responseProfilepaymentUpdate?.data?.error === 0) {
            localStorage.setItem('CARWASH_OWNER_PAYMENTDATA_ADDED', 'true')
            setPaymentinfoErrors({
                accountholdername: '',
                accountnumber: '',
                retypeaccountnumber: '',
                routingnumber: '',
                accountholdertype:'',
            });
            setPaymentinfoFormData({
                ...paymentinfoformData,
                accountholdername: '',
                accountnumber: '',
                routingnumber: '',
                accountholdertype:1,
            });
            getCompanyBank();
           // handleShowAddSuccessModal();
          //  toaster.toastSuccess("Your Payment Info Updated Successfully.");
        }
        else {
            toaster.toastError(responseProfilepaymentUpdate?.data?.message);
        }
    }

    const [BankAccountNextStepSuccessModal, setBankAccountNextStepSuccessModal] = useState(false);
    const handleOnHide = () => {
        handleCloseAddSuccessModal();
    }
    const handleCloseAddSuccessModal = () => {
        setBankInformationVerificationStatus(false)
        setBankAccountNextStepSuccessModal(false);
        checkBankInfoVerificationStatus()
    }
    const handleShowAddSuccessModal = () => {
        setBankAccountNextStepSuccessModal(true);
        
    }



    // Attach Bank Info to stripe
    const attachBankAccountToStripe = async (clientSecret : string) => {
        if(stripe) {
            return await stripe.confirmUsBankAccountSetup(
                clientSecret,
                {
                    payment_method: {
                        us_bank_account: {
                            account_number: paymentinfoformData.accountnumber,
                            routing_number: paymentinfoformData.routingnumber,
                            account_holder_type: (paymentinfoformData.accountholdertype === 1) ? 'individual' : 'company',
                        },
                        billing_details: {
                            name: paymentinfoformData.accountholdername,
                            email: profileformData.email
                        },
                    },
                }
            )
        } else {
            return null;
        }
    }

    //Check status of bank verification process
    const checkBankInfoVerificationStatus = async ()=> {
        setLoading(true);
        const bankInformationVerificationResult:any = await axios.get(`${UsersApi.CARWASH_OWNER_BANK_VERIFICATION_CHECKING}/${locationId}`);
        if(bankInformationVerificationResult?.data?.error === 0) {
            if (bankInformationVerificationResult?.data?.data?.status === "requires_payment_method") {
                toaster.toastError("Confirmation failed. Please try again with a different bank account.");
                setBankInformationVerificationStatus(true)
                setBankInformationVerificationLink(null)
            }  else if(bankInformationVerificationResult?.data?.data?.nextAction?.type === "verify_with_microdeposits"){
                setBankInformationVerificationStatus(false)
                setBankInformationVerificationLink(bankInformationVerificationResult?.data?.data?.nextAction?.verify_with_microdeposits?.hosted_verification_url)
            } else if (bankInformationVerificationResult?.data?.data?.status === "succeeded") {
                localStorage.setItem('CARWASH_OWNER_PAYMENTDATA_ADDED', 'true')
                setBankInformationVerificationStatus(true)
                setBankInformationVerificationLink(null)
                getCompanyBank();
            } else {
                setBankInformationVerificationStatus(true)
            }
        }
        setLoading(false);
    }


    // const [visible, setVisible] = useState(true);
    useEffect(() => {
        if(bankInformationVerificationStatus === false && selectedTab === 'paymentinfo') {
            const intervalId = setInterval(() => checkBankInfoVerificationStatus(), 60000);
            return () => {
                clearInterval(intervalId);
            };
        }
    }, [bankInformationVerificationStatus,selectedTab]);

    //Tab Changes
    const changeTabs = async (selectedTab : any) => {
        setSelectedTab(selectedTab)
    }

    const openVerificationLink = (verificationLink:any) => {
        setBankInformationVerificationStatus(false)
        setBankAccountNextStepSuccessModal(false);
        window.open(verificationLink, '_blank')
    }

    return (

        <div className='manage-subscription-container'>
            <Container>

                <div className='cw-form-outer-wrap'>
                    <div className="cw-main-tab">
                        <Tabs
                            defaultActiveKey={defaulttab}
                            id="profile-tab"
                            className="cw-tab-title justify-content-end"
                            onSelect={(k) => changeTabs(k)}   
                        >
                            <Tab eventKey="profile" title="Profile">
                                <form onSubmit={profilehandleSubmit} ref={formRef}>
                                    <div className="cw-form-container">
                                        <div className="form-inner mx-auto">
                                        {!(userInfo?.userTypeId === 4 && userInfo?.permissionType === 2) 
                                        &&<>
                                            <h5>Car Wash Information</h5>
                                            <Row>
                                                <Col md={12}>
                                                    <CWInput
                                                        id="washname"
                                                        name="washname"
                                                        type="text"
                                                        label="Car Wash Name"
                                                        value={profileformData.washname}
                                                        onChange={onprofileInputChange}
                                                        maxLength={WASH_NAME_MAX_LENGTH}
                                                        errorMessage={profileerrors.washname}
                                                        autoFocus
                                                        isMandatory
                                                        autoComplete='off'

                                                    />
                                                </Col>
                                            </Row>
                                            <Row>
                                                <Col md={6}>
                                                    <CWInput
                                                        id="address1"
                                                        name="address1"
                                                        type="text"
                                                        label="Address 1"
                                                        value={profileformData.address1}
                                                        onChange={onprofileInputChange}
                                                        maxLength={WASH_ADDRESS_MAX_LENGTH}
                                                        errorMessage={profileerrors.address1}
                                                        autoFocus
                                                        isMandatory
                                                        autoComplete='off'

                                                    />
                                                </Col>
                                                <Col md={6}>
                                                    <CWInput
                                                        id="address2"
                                                        name="address2"
                                                        type="text"
                                                        label="Address 2"
                                                        labelSuffix="(Optional)"
                                                        value={profileformData.address2}
                                                        onChange={onprofileInputChange}
                                                        maxLength={WASH_ADDRESS_MAX_LENGTH}
                                                        errorMessage={profileerrors.address2}
                                                        autoFocus
                                                        autoComplete='off'

                                                    />
                                                </Col>
                                            </Row>
                                            <Row>
                                            <Col md={6}>
                                                <CWInput
                                                    id="city"
                                                    name="city"
                                                    type="text"
                                                    label="City"
                                                    value={profileformData.city}
                                                    onChange={onprofileInputChange}
                                                    maxLength={CITY_MAX_LENGTH}
                                                    errorMessage={profileerrors.city}
                                                    isMandatory
                                                />
                                            </Col>
                                            <Col md={6}>
                                                <CWSelect
                                                    id="country"
                                                    name="country"
                                                    options={countryOptions}
                                                    values={getSelectedOptions(countryOptions, profileformData.country)}
                                                    label="Country"
                                                    dropdownGap={0}
                                                    dropdownPosition="auto"
                                                    placeholder="Select"
                                                    multi={false}
                                                    searchable={false}
                                                    disabled
                                                    isMandatory
                                                    errorMessage={profileerrors.country}
                                                    onChange={(selectedOptions) => handleSelectChange(selectedOptions[0], 'country')}
                                                    className="no-input"
                                                />
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col md={6}>
                                                <CWSelect
                                                    id="state"
                                                    name="state"
                                                    options={options}
                                                    value={profileformData.state}
                                                    values={getSelectedOptions(options, profileformData.state)}
                                                    label={stateLabel}
                                                    dropdownGap={0}
                                                    dropdownPosition="auto"
                                                    placeholder="Select"
                                                    multi={false}
                                                    isMandatory
                                                    errorMessage={profileerrors.state}
                                                    onChange={(selectedOptions) => handleSelectChange(selectedOptions[0], 'state')}
                                                    className="no-input"
                                                />
                                            </Col>
                                            <Col md={6}>
                                                <CWInput
                                                    id="zipcode"
                                                    name="zipcode"
                                                    type="text"
                                                    label={postalLabel}
                                                    value={profileformData.zipcode}
                                                    onChange={onprofileInputChange}
                                                    maxLength={postalMaxLength}
                                                    errorMessage={profileerrors.zipcode}
                                                    isMandatory
                                                />
                                            </Col>
                                        </Row>
                                        </>}
                                            <h5 className='mt-3'>Personal Information</h5>
                                            <Row>
                                                <Col md={6}>
                                                    <CWInput
                                                        id="name"
                                                        name="name"
                                                        type="text"
                                                        label="Name"
                                                        value={profileformData.name}
                                                        onChange={onprofileInputChange}
                                                        maxLength={NAME_MAX_LENGTH}
                                                        autoFocus
                                                        isMandatory
                                                        errorMessage={profileerrors.name}
                                                        autoComplete='off'

                                                    />
                                                </Col>
                                                <Col md={6}>
                                                    <div className="custom-note position-relative">
                                                        <CWInput
                                                            id="email"
                                                            name="email"
                                                            type="text"
                                                            label="Email Address"
                                                            value={profileformData.email}
                                                            onChange={onprofileInputChange}
                                                            maxLength={EMAIL_MAX_LENGTH}
                                                            errorMessage={profileerrors.email}
                                                            isMandatory
                                                            autoComplete="off"
                                                            disabled
                                                        />
                                                        <span className="note p-r">This will be used for Signing in to the website</span>
                                                    </div>
                                                </Col>
                                            </Row>
                                            {!(userInfo?.userTypeId === 4 && userInfo?.permissionType === 2) 
                                            &&
                                            <Row>
                                                <Col md={6}>
                                                    <CWInput
                                                        id="phonenumber"
                                                        name="phonenumber"
                                                        type="text"
                                                        value={profileformData.phonenumber}
                                                        label="Phone Number"
                                                        isMandatory
                                                        maxLength={PHONE_MAX_LENGTH}
                                                        errorMessage={profileerrors.phonenumber}
                                                        onPaste={handlePaste}
                                                        onChange={onprofileInputChange}
                                                    />
                                                </Col>
                                            </Row>
                                            }
                                            <div className="btn-sec d-flex justify-content-end">
                                                <Button title="Cancel" variant="secondary" className="secondary-btn" onClick={() => navigate(`/${pageURLs.dashboard}`)}>Cancel</Button>
                                                <Button title="Save" variant="primary" className="primary-btn" type='submit'>Save</Button>
                                            </div>
                                        </div>

                                    </div>
                                </form>
                            </Tab>
                            <Tab eventKey="changepassword" title="Change Password">
                                <form onSubmit={passwordhandleSubmit} ref={form3Ref}>
                                    <div className="cw-form-container">
                                        <div className="form-inner mx-auto">
                                            <h5>Change Password</h5>
                                            <Row>
                                                <Col md={6}>
                                                    <CWPasswordInput
                                                        id="oldpassword"
                                                        name="oldpassword"
                                                        type={'password'}
                                                        label="Old Password"
                                                        showpass={<span />}
                                                        value={passwordformData.oldpassword}
                                                        onChange={onpasswordInputChange}
                                                        maxLength={PASSWORD_MAX_LENGTH}
                                                        isMandatory
                                                        autoComplete="off"
                                                        errorMessage={passworderrors.oldpassword}
                                                    />
                                                </Col>
                                                <Col md={6}>
                                                    <CWPasswordInput
                                                        id="newpassword"
                                                        name="newpassword"
                                                        type={'password'}
                                                        label="New Password"
                                                        showpass={<span />}
                                                        value={passwordformData.newpassword}
                                                        onChange={onpasswordInputChange}
                                                        maxLength={PASSWORD_MAX_LENGTH}
                                                        isMandatory
                                                        autoComplete='off'
                                                        errorMessage={passworderrors.newpassword}
                                                    />
                                                </Col>
                                            </Row>
                                            <Row>
                                                <Col md={6}>
                                                    <CWPasswordInput
                                                        id="retypepassword"
                                                        name="retypepassword"
                                                        type={'password'}
                                                        label="Re-Type Password"
                                                        showpass={<span />}
                                                        value={passwordformData.retypepassword}
                                                        onChange={onpasswordInputChange}
                                                        maxLength={PASSWORD_MAX_LENGTH}
                                                        isMandatory
                                                        autoComplete='off'
                                                        errorMessage={passworderrors.retypepassword}
                                                    />
                                                </Col>
                                            </Row>
                                            <div className="btn-sec d-flex justify-content-end">
                                                <Button title="Cancel" variant="secondary" className="secondary-btn" onClick={() => navigate(`/${pageURLs.dashboard}`)}>Cancel</Button>
                                                <Button title="Save" variant="primary" className="primary-btn" type='submit'>Save</Button>
                                            </div>
                                        </div>

                                    </div>
                                </form>
                            </Tab>
                        </Tabs>
                    </div>
                </div>

            </Container>
            <Modal show={BankAccountNextStepSuccessModal} onHide={handleOnHide} centered className='account-info-modal'>
                <Modal.Body>
                <h4 className='f-bold'>Bank account has been added successfully!</h4>
                <h6 className='f-semi'>To complete your payment to car wash app.com, please verify your bank account with the following steps:</h6>
                <p className='f-medium'>1. Login to your bank account and view the recent transactions of your account: {bankAccountLast4Digit}.</p>
                <p className='f-medium'>2. Search for the micro deposit amount received from car wash app.com.</p>
                <p className='f-medium'>3. Locate the 6-digit transaction code in the micro deposit amount’s transaction description starting with “SM”.</p>
                <p className='f-medium'>4. Verify your account and complete the payment by entering the code on the Secure Verification page. <a href="javascript:void(0)"  onClick={ () => openVerificationLink(bankInformationVerificationLink)}>Verify Now</a>.</p>
                <Button title="Proceed To Setup" variant="primary" className='primary-btn' onClick={handleOnHide}>OKAY</Button>
                </Modal.Body>
            </Modal>
            {loading && <SmallLoader />}
        </div>
    )
}

export default CWOMyProfile;
